import { cn, formatCurrency } from '@hbs-us/ui';

import type { PaletteRow } from '../../features/search/buildGroups.js';
import { highlightTerms } from '../../features/search/highlight.js';
import { SEARCH_CATALOG } from '../../features/search/searchCatalog.js';

/**
 * The three things a search row can be, and the type filter above them.
 *
 * A row is a BUTTON that also answers to the keyboard, because the panel drives
 * selection from the input: the marker follows `aria-selected`, and the row
 * itself never takes focus. Its own background stays transparent — the one
 * travelling marker behind the list is what paints the selection, so a row that
 * also filled itself would double the highlight and read as a flicker.
 */

const ROW_CLASS = 'relative z-10 flex w-full items-center gap-3 px-4 py-2 text-left';

/**
 * Status is a word and a dot, never a pill. Nine record types wearing tinted
 * chips would turn the panel into a dashboard of labels; the dot carries the
 * state and the word carries the meaning.
 *
 * The keys are lowercased on lookup because the tables disagree on case — jobs
 * store `Active`, invoices store `paid`. An unmapped status still renders, in
 * the neutral tone, so a state nobody has coloured yet is never invisible.
 */
const STATUS_TONE: Record<string, string> = {
  paid: 'bg-success',
  active: 'bg-success',
  approved: 'bg-success',
  accepted: 'bg-success',
  unpaid: 'bg-warning',
  pending: 'bg-warning',
  sent: 'bg-warning',
  draft: 'bg-warning',
  overdue: 'bg-destructive',
  declined: 'bg-destructive',
};

export interface PaletteRowButtonProps {
  id: string;
  row: PaletteRow;
  /** The typed words, for the match highlight. */
  query: string;
  locale: string;
  selected: boolean;
  onSelect: () => void;
  onHover: () => void;
  /** Resolved "Show N more" text; only read for a `more` row. */
  moreLabel: string;
}

export function PaletteRowButton({
  id,
  row,
  query,
  locale,
  selected,
  onSelect,
  onHover,
  moreLabel,
}: PaletteRowButtonProps) {
  const shared = {
    id,
    type: 'button' as const,
    role: 'option',
    'aria-selected': selected,
    onClick: onSelect,
    onMouseMove: onHover,
    tabIndex: -1,
  };

  if (row.kind === 'more') {
    return (
      <button {...shared} className={cn(ROW_CLASS, 'font-medium text-muted-foreground text-sm')}>
        {/* Aligned to the titles above it, not to their icons: it is a continuation
            of the list, not another record in it. */}
        <span className="pl-7">{moreLabel}</span>
      </button>
    );
  }

  if (row.kind === 'destination') {
    const Icon = row.destination.icon;
    return (
      <button {...shared} className={ROW_CLASS}>
        <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground text-sm">
          {highlightTerms(row.destination.label, query)}
        </span>
      </button>
    );
  }

  const { hit } = row;
  const Icon = SEARCH_CATALOG[hit.type].icon;
  const tone = hit.status ? STATUS_TONE[hit.status.toLowerCase()] : undefined;

  return (
    <button {...shared} className={ROW_CLASS}>
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-foreground text-sm">
          {highlightTerms(hit.title, query)}
        </span>
        {hit.subtitle ? (
          <span className="mt-0.5 block truncate text-muted-foreground text-xs">
            {highlightTerms(hit.subtitle, query)}
          </span>
        ) : null}
      </span>
      {hit.amount !== null || hit.status ? (
        <span className="flex shrink-0 flex-col items-end gap-0.5 text-right">
          {hit.amount !== null ? (
            <span className="font-mono text-foreground text-xs tabular-nums">
              {formatCurrency(hit.amount, locale)}
            </span>
          ) : null}
          {hit.status ? (
            <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
              <span
                aria-hidden="true"
                className={cn('size-1.5 shrink-0 rounded-full', tone ?? 'bg-muted-foreground/40')}
              />
              {hit.status}
            </span>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}

export interface FilterButtonProps {
  label: string;
  count: number;
  /** The server stopped counting here, so the number reads "and more". */
  capped: boolean;
  pressed: boolean;
  onPress: () => void;
}

/**
 * A type filter. It is a toggle button, not a tab: nothing swaps panels, one
 * list narrows, and `aria-pressed` is the state that actually describes it.
 *
 * There is no disabled state, because the panel only ever offers a type that
 * found something. A row of greyed-out zeroes was both a dead control and the
 * thing that pushed the row past the panel's width.
 */
export function FilterButton({ label, count, capped, pressed, onPress }: FilterButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onPress}
      className={cn(
        'shrink-0 whitespace-nowrap px-2.5 pt-3 pb-3.5 font-medium text-sm transition-colors',
        'duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'focus-visible:ring-offset-1 focus-visible:ring-offset-card',
        pressed ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
      <span
        className={cn(
          'ml-1.5 font-mono text-[0.6875rem]',
          pressed ? 'text-primary-ink' : 'text-muted-foreground',
        )}
      >
        {capped ? `${count}+` : count}
      </span>
    </button>
  );
}

export function KeyHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {keys.map((key) => (
        <kbd
          key={key}
          className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[0.6875rem]"
        >
          {key}
        </kbd>
      ))}
      {label}
    </span>
  );
}
